import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo } from "react";
import {
    InvoicesByCounterpartyItem,
    InvoiceStatus,
} from "@common/types/invoice";
import { InvoiceFilters } from "@common/contracts/invoice.contract";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { Amount } from "@design-system/Amount/Amount";
import { InvoicesByCustomerColumn } from "@common/types/views/invoiceViews";
import classNames from "classnames";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import styles from "./DrilldownAmountCell.module.scss";

interface InvoicesByCustomerViewCallbacks {
    onDrilldown?: (params: InvoiceFilters) => void;
}

const getDrilldownParams = (
    item: InvoicesByCounterpartyItem,
    columnType?: InvoicesByCustomerColumn,
): InvoiceFilters => {
    const baseParams: InvoiceFilters =
        item.counterpartyId === null
            ? { withEmptyOrUnverifiedCounterparty: true }
            : { counterpartiesIds: [item.counterpartyId] };

    // Filter by externalStatus to align with the by-counterparty aggregation,
    // which buckets non-accrual entities by the effective status (internal
    // sources resolve to `status`, integrations to `externalStatus`).
    switch (columnType) {
        case InvoicesByCustomerColumn.CURRENT:
            return {
                ...baseParams,
                externalInvoiceStatuses: [InvoiceStatus.OPEN],
                overdue: false,
            };
        case InvoicesByCustomerColumn.PAST_DUE_1_TO_30:
            return {
                ...baseParams,
                externalInvoiceStatuses: [InvoiceStatus.OPEN],
                overdue: true,
                minOverdue: 1,
                maxOverdue: 30,
            };
        case InvoicesByCustomerColumn.PAST_DUE_31_TO_60:
            return {
                ...baseParams,
                externalInvoiceStatuses: [InvoiceStatus.OPEN],
                overdue: true,
                minOverdue: 31,
                maxOverdue: 60,
            };
        case InvoicesByCustomerColumn.PAST_DUE_61_TO_90:
            return {
                ...baseParams,
                externalInvoiceStatuses: [InvoiceStatus.OPEN],
                overdue: true,
                minOverdue: 61,
                maxOverdue: 90,
            };
        case InvoicesByCustomerColumn.PAST_DUE_91_PLUS:
            return {
                ...baseParams,
                externalInvoiceStatuses: [InvoiceStatus.OPEN],
                overdue: true,
                minOverdue: 91,
            };
        case InvoicesByCustomerColumn.TOTAL_OPEN:
            return {
                ...baseParams,
                externalInvoiceStatuses: [InvoiceStatus.OPEN],
            };
        case InvoicesByCustomerColumn.TOTAL_PAID:
            return {
                ...baseParams,
                externalInvoiceStatuses: [InvoiceStatus.PAID],
            };
        case InvoicesByCustomerColumn.INVOICES_COUNT:
        default:
            return baseParams;
    }
};

export const DrilldownAmountCell = memo(
    ({
        getValue,
        row,
        table,
        column,
    }: CellContext<ViewTableFeatures, InvoicesByCounterpartyItem, number>) => {
        const amount = getValue();
        const item = row.original;
        const { callbacks } = useViewTableMeta<
            InvoicesByCustomerViewCallbacks,
            unknown
        >(table);
        const onDrilldown = callbacks?.onDrilldown;

        const columnType = column.id as InvoicesByCustomerColumn;

        const shouldFormatCurrency =
            columnType !== InvoicesByCustomerColumn.INVOICES_COUNT;

        if (amount.toString() === "0") {
            return (
                <ViewTableCell
                    className={classNames(
                        styles.drilldownAmountCell,
                        styles[columnType],
                    )}
                >
                    <EditableCellContent readonly className={styles.content}>
                        <span className={styles.zeroAmount}>-</span>
                    </EditableCellContent>
                </ViewTableCell>
            );
        }

        const handleClick = () => {
            if (!onDrilldown) {
                return;
            }
            const params = getDrilldownParams(item, columnType);
            onDrilldown(params);
        };

        return (
            <ViewTableCell
                captureClicks
                className={classNames(
                    styles.drilldownAmountCell,
                    styles[columnType],
                )}
            >
                <EditableCellContent
                    onClick={handleClick}
                    readonly={!onDrilldown}
                    className={styles.content}
                    size="sm"
                >
                    {shouldFormatCurrency ? (
                        <Amount amount={amount} />
                    ) : (
                        <span>{amount}</span>
                    )}
                </EditableCellContent>
            </ViewTableCell>
        );
    },
);
